// AdminAutoFlattenPage — Cron job monitor, manual force-flatten
// Shows last run, next run, accounts that got flattened, force actions

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

interface FlattenRun {
  id: string;
  date: string;
  time: string;
  trigger: 'auto' | 'manual';
  triggerBy: string;
  accountsAffected: number;
  positionsClosed: number;
  status: 'success' | 'partial' | 'failed';
  duration: number;     // ms
  error?: string;
}

const RUNS: FlattenRun[] = [
  { id: 'f1', date: '2026-07-24', time: '16:00 CT', trigger: 'auto',   triggerBy: 'cron',  accountsAffected: 184, positionsClosed: 412, status: 'success', duration: 8400 },
  { id: 'f2', date: '2026-07-23', time: '16:00 CT', trigger: 'auto',   triggerBy: 'cron',  accountsAffected: 198, positionsClosed: 387, status: 'success', duration: 8200 },
  { id: 'f3', date: '2026-07-22', time: '16:00 CT', trigger: 'auto',   triggerBy: 'cron',  accountsAffected: 162, positionsClosed: 295, status: 'success', duration: 7800 },
  { id: 'f4', date: '2026-07-22', time: '11:45 CT', trigger: 'manual', triggerBy: 'admin@funderpro.live', accountsAffected: 1,   positionsClosed: 3,   status: 'partial', duration: 1200, error: '1 position slippage exceeded 1%' },
  { id: 'f5', date: '2026-07-21', time: '16:00 CT', trigger: 'auto',   triggerBy: 'cron',  accountsAffected: 0,   positionsClosed: 0,   status: 'failed',  duration: 200,  error: 'Broker API timeout' },
];

export function AdminAutoFlattenPage() {
  const nav = useNavigate();
  const [runs] = useState(RUNS);
  const [forceAccount, setForceAccount] = useState('');
  const [confirming, setConfirming] = useState(false);

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white pb-8">
      <div className="sticky top-0 z-10 flex h-11 items-center justify-between border-b border-white/10 bg-[#0A0A0A]/80 backdrop-blur-xl px-2">
        <button onClick={() => nav('/admin/dashboard')} data-testid="back" className="flex h-9 w-9 items-center justify-center text-[#0A84FF] haptic">
          <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2.4"><path d="M15 5l-7 7 7 7" strokeLinecap="round" strokeLinejoin="round" /></svg>
        </button>
        <div className="absolute left-1/2 -translate-x-1/2 text-[15px] font-semibold">Auto-flatten</div>
        <div className="w-9" />
      </div>

      {/* Hero */}
      <div className="mx-4 mt-3 rounded-[16px] border border-white/10 bg-gradient-to-br from-[#5856D6] to-[#1C1C1E] p-4 text-center">
        <div className="text-[11px] uppercase tracking-wider" style={{ color: 'rgba(255,255,255,0.85)' }}>Cron status</div>
        <div className="mt-1 text-[24px] font-bold tabular-nums">Next: 16:00 CT</div>
        <p className="text-[12px] mt-1" style={{ color: 'rgba(255,255,255,0.85)' }}>Daily flatten job runs automatically</p>
        <div className="mt-2 inline-flex items-center gap-1 rounded-full bg-[#34C759] px-3 py-1 text-[10px] font-bold uppercase">✓ Healthy · 5 successful runs</div>
      </div>

      {/* Stats */}
      <div className="mx-4 mt-3 grid grid-cols-3 gap-2">
        <div className="rounded-[10px] bg-white/5 border border-white/10 p-2 text-center">
          <div className="text-[9px] text-white/60 uppercase">Today</div>
          <div className="text-[16px] font-bold text-[#34C759]">184</div>
          <div className="text-[8px] text-white/40">accounts</div>
        </div>
        <div className="rounded-[10px] bg-white/5 border border-white/10 p-2 text-center">
          <div className="text-[9px] text-white/60 uppercase">Positions</div>
          <div className="text-[16px] font-bold">412</div>
          <div className="text-[8px] text-white/40">closed</div>
        </div>
        <div className="rounded-[10px] bg-white/5 border border-white/10 p-2 text-center">
          <div className="text-[9px] text-white/60 uppercase">Duration</div>
          <div className="text-[16px] font-bold tabular-nums">8.4s</div>
          <div className="text-[8px] text-white/40">avg</div>
        </div>
      </div>

      {/* Manual force flatten */}
      <div className="mx-4 mt-3 rounded-[14px] border border-white/10 bg-white/5 p-4">
        <h2 className="text-[14px] font-bold text-white">Manual force-flatten</h2>
        <p className="text-[11px] text-white/50 mt-0.5">Immediately close all open positions for a specific account</p>
        <div className="mt-2 flex gap-2">
          <input
            value={forceAccount}
            onChange={(e) => setForceAccount(e.target.value)}
            placeholder="Account ID (e.g. FP-100K-14283)"
            data-testid="force-account-input"
            className="flex-1 h-9 rounded-[8px] bg-white/5 border border-white/10 px-2.5 text-[12px] text-white placeholder-white/30"
          />
          <button onClick={() => setConfirming(true)} data-testid="force-flatten" className="h-9 px-4 rounded-full bg-[#FF3B30] text-white text-[12px] font-bold active:opacity-40">Flatten</button>
        </div>
      </div>

      {/* Runs log */}
      <h2 className="mt-5 px-4 pb-2 text-[13px] font-normal uppercase tracking-wide text-white/50">Recent runs</h2>
      <div className="px-4 space-y-2">
        {runs.map((r) => {
          const sColor = r.status === 'success' ? '#34C759' : r.status === 'partial' ? '#FF9500' : '#FF3B30';
          return (
            <div key={r.id} data-testid={`run-${r.id}`} className="rounded-[12px] border border-white/10 bg-white/5 p-3">
              <div className="flex items-center gap-3">
                <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-[8px] text-[16px]" style={{ backgroundColor: sColor + '20', color: sColor }}>{r.status === 'success' ? '✓' : r.status === 'partial' ? '⚠' : '⛔'}</div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5">
                    <span className="text-[12px] font-bold text-white">{r.date} {r.time}</span>
                    <span className="rounded-full px-1.5 py-0.5 text-[8px] font-bold uppercase" style={{ backgroundColor: (r.trigger === 'auto' ? '#0A84FF' : '#FF9500') + '20', color: r.trigger === 'auto' ? '#0A84FF' : '#FF9500' }}>{r.trigger}</span>
                  </div>
                  <div className="text-[10px] text-white/50">by {r.triggerBy} · {r.accountsAffected} accounts · {r.positionsClosed} positions · {r.duration}ms</div>
                  {r.error && <div className="text-[10px] mt-0.5" style={{ color: sColor }}>⚠ {r.error}</div>}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {confirming && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4" onClick={() => setConfirming(false)}>
          <div className="w-full max-w-[380px] rounded-[20px] bg-[#1C1C1E] p-4" onClick={(e) => e.stopPropagation()}>
            <h2 className="text-[18px] font-bold mb-1">Force-flatten {forceAccount}?</h2>
            <p className="text-[12px] text-white/60 mb-3">This will immediately close all open positions for this account at market price. Action is irreversible and logged in audit.</p>
            <div className="flex gap-2">
              <button onClick={() => setConfirming(false)} className="flex-1 h-10 rounded-full bg-white/10 text-[13px] font-semibold active:opacity-40">Cancel</button>
              <button onClick={() => { alert('Flattened. Audit logged.'); setConfirming(false); setForceAccount(''); }} data-testid="confirm-force" className="flex-1 h-10 rounded-full bg-[#FF3B30] text-white text-[13px] font-bold active:opacity-40">Force flatten</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
